<script lang="ts">
import { defineComponent, computed } from 'vue';

import useContext from '@/composables/useContext';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import useFormatting from '@/composables/useFormatting';
import useUser from '@/composables/useUser';

import ChannelSwitcher from '@/components/ChannelSwitcher.vue';

export default defineComponent({
  name: 'SiteHeaderRibbon',

  components: {
    ChannelSwitcher,
  },

  data() {
    const { channel } = useContext();
    const { websiteText } = useWebsiteTexts();
    const { renderMarkdown } = useFormatting();
    const { organizationName, hasMultipleOrganizations } = useUser();

    return {
      channel,
      websiteText,
      renderMarkdown,
      organizationName,
      hasMultipleOrganizations,
    };
  },

  computed: {
    contactLink(): string | undefined {
      return this.channel?.value?.website?.fields?.ContactLink;
    },

    promotionTextHtml(): string {
      const ribbonText = this.channel?.value?.frameworkContentJson?.RibbonText;
      if (!ribbonText) return '';
      return this.renderMarkdown(ribbonText);
    },
  },
});
</script>

<template>
  <div
    :class="[
      'site-header-ribbon',
      {
        'site-header-ribbon__hide-on-phone': !promotionTextHtml && !organizationName,
        'site-header-ribbon__has-multiple-organizations': hasMultipleOrganizations,
      },
    ]"
  >
    <div class="site-header-ribbon__content">
      <div class="site-header-ribbon__left site-header-ribbon__hide-on-phone">
        <router-link
          class="site-header-ribbon__contact-link"
          v-if="contactLink"
          :to="contactLink"
        >
          {{ websiteText("global__label_contact_link").value }}
        </router-link>
      </div>
      <div
        class="site-header-ribbon__middle text-editor-content"
        v-html="promotionTextHtml"
        v-if="!hasMultipleOrganizations"
      ></div>
      <div
        v-else
        class="site-header-ribbon__middle text-editor-content site-header-ribbon__shopping-as"
      >
        <span
          >{{ websiteText("global__shopping_as").value }}
          <strong>{{ organizationName }}</strong></span
        >
      </div>
      <div class="site-header-ribbon__right site-header-ribbon__hide-on-phone">
        <channel-switcher class="site-header-ribbon__channel-switcher" />
      </div>
    </div>
  </div>
</template>

<style>
.site-header--transparent.site-header--active .site-header-ribbon {
  background-color: white;
}

.site-header--foreground-white .site-header-ribbon__content {
  color: var(--color-neutrals-00);
}

.site-header-ribbon {
  transition: background-color linear 0.2s;
  background-color: white;
  border-bottom: 1px solid var(--color-neutrals-20);
  padding: 0 15px;
}

.site-header-ribbon__left,
.site-header-ribbon__right {
  flex-basis: 300px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.site-header-ribbon__right {
  justify-content: flex-end;
}

.site-header-ribbon__middle {
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.site-header--transparent .site-header-ribbon {
  background-color: transparent;
}

.site-header-ribbon__has-multiple-organizations {
  background-color: var(--color-brand-juniper-100);
}

.site-header-ribbon__has-multiple-organizations .site-header-ribbon__channel-switcher,
.site-header-ribbon__has-multiple-organizations .site-header-ribbon__contact-link,
.site-header-ribbon__has-multiple-organizations .site-header-ribbon__shopping-as {
  color: white;
}

.site-header-ribbon__shopping-as {
  max-width: calc(100vw - 2rem);
}

.site-header-ribbon__shopping-as span {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
  line-height: 18px;
}

.site-header-ribbon__content {
  display: flex;
  height: 34px;
  max-width: var(--header-max-width);
  margin: 0 auto;
  justify-content: space-between;
  font-size: 14px;
}

@media (--phone) {
  .site-header-ribbon__hide-on-phone {
    display: none;
  }
}
</style>
